// rsaPathUtils.js — path/geometry utility functions shared by RSAModelAnalysis and RSAExistingAnalysis

export function getWaypointCoordinateIndices(pathData, waypoints) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return [];
  const coords = pathData.geometry.coordinates;
  const indices = [];
  for (const wp of waypoints) {
    const lat = wp.lat;
    const lng = wp.lng;
    let bestIdx = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < coords.length; i++) {
      const c = coords[i];
      const clat = Array.isArray(c) ? c[1] : c.lat;
      const clng = Array.isArray(c) ? c[0] : c.lng;
      const d2 = (clat - lat) ** 2 + (clng - lng) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestIdx = i;
      }
    }
    indices.push(bestIdx);
  }
  return indices;
}

/** Slice pathData to the segment between waypoint startIdx and endIdx (inclusive) */
export function slicePathDataByWaypointRange(pathData, waypoints, startIdx, endIdx) {
  if (!pathData?.geometry?.coordinates?.length || !waypoints?.length) return null;
  const coords = pathData.geometry.coordinates;
  const wpIndices = getWaypointCoordinateIndices(pathData, waypoints);
  const i0 = Math.min(wpIndices[startIdx] ?? 0, wpIndices[endIdx] ?? coords.length - 1);
  const i1 = Math.max(wpIndices[startIdx] ?? 0, wpIndices[endIdx] ?? coords.length - 1);
  const sliced = coords.slice(i0, i1 + 1);
  if (sliced.length < 2) return null;
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: sliced },
    properties: {},
  };
}

// Segment indicator colors (match map waypoint markers when segment is active)
export const SEGMENT_COLORS = [
  '#DD4130', /* red */
  '#2563eb', /* blue */
  '#059669', /* emerald */
  '#7c3aed', /* violet */
  '#ea580c', /* orange */
  '#0d9488', /* teal */
  '#dc2626', /* red-6 */
  '#ca8a04', /* yellow/amber */
  '#db2777', /* pink */
  '#0891b2', /* cyan */
];

export function getNextSegmentColorIndex(segments = []) {
  const usedColorIndices = new Set((segments || []).map((seg) => (seg?.colorIndex ?? 0)));
  for (let i = 0; i < SEGMENT_COLORS.length; i += 1) {
    if (!usedColorIndices.has(i)) return i;
  }
  return 0;
}

export function parseLinearMetersPerUnitFromWkt(wkt) {
  if (!wkt || typeof wkt !== 'string') return null;
  const re = /UNIT\["[^"]+"\s*,\s*([0-9.+\-eE]+)\s*\]/g;
  let match;
  let last = null;
  while ((match = re.exec(wkt)) !== null) {
    const v = Number(match[1]);
    if (Number.isFinite(v) && v > 0) last = v;
  }
  return last;
}

export function parseUnitsTokenFromProj4(proj4String) {
  if (!proj4String || typeof proj4String !== 'string') return '';
  const m = proj4String.match(/\+units=([^\s]+)/i);
  return (m?.[1] || '').toLowerCase();
}

export function getProjectedUnitsPerFoot({ wkt, proj4String }) {
  const metersPerUnitFromWkt = parseLinearMetersPerUnitFromWkt(wkt);
  if (Number.isFinite(metersPerUnitFromWkt) && metersPerUnitFromWkt > 0) {
    return 0.3048 / metersPerUnitFromWkt;
  }
  const token = parseUnitsTokenFromProj4(proj4String);
  if (token === 'us-ft' || token === 'ft') return 1;
  if (token === 'm') return 0.3048;
  return 0.3048;
}

export function normalizeVector2(x, y) {
  const len = Math.hypot(x, y);
  if (!Number.isFinite(len) || len <= 1e-9) return [0, 0];
  return [x / len, y / len];
}

export function offsetPolylineXY(polylineXY, signedOffsetUnits) {
  if (!Array.isArray(polylineXY) || polylineXY.length < 2) return [];
  const pts = polylineXY.map(([x, y]) => [Number(x), Number(y)]);
  if (pts.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) return [];
  const n = pts.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const p = pts[i];
    let tx = 0;
    let ty = 0;
    if (i === 0) {
      tx = pts[1][0] - p[0];
      ty = pts[1][1] - p[1];
    } else if (i === n - 1) {
      tx = p[0] - pts[n - 2][0];
      ty = p[1] - pts[n - 2][1];
    } else {
      const [p0, p1, p2] = [pts[i - 1], p, pts[i + 1]];
      const [aX, aY] = normalizeVector2(p1[0] - p0[0], p1[1] - p0[1]);
      const [bX, bY] = normalizeVector2(p2[0] - p1[0], p2[1] - p1[1]);
      tx = aX + bX;
      ty = aY + bY;
      if (Math.hypot(tx, ty) <= 1e-6) {
        tx = bX || aX;
        ty = bY || aY;
      }
    }
    const [nx, ny] = normalizeVector2(-ty, tx); // left-hand normal
    out.push([p[0] + (signedOffsetUnits * nx), p[1] + (signedOffsetUnits * ny)]);
  }
  return out;
}

export function clampNumber(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export function projectLatLngToLocalMeters(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const origin = points[0];
  const lat0Rad = (origin.lat * Math.PI) / 180;
  const cosLat = Math.max(0.2, Math.cos(lat0Rad));
  return points.map((p) => ({
    x: (p.lng - origin.lng) * 111320 * cosLat,
    y: (p.lat - origin.lat) * 110540,
  }));
}

export function getDistanceMetersXY(a, b) {
  return Math.hypot((b.x - a.x), (b.y - a.y));
}

export function computeCurvatureScores(projectedPoints) {
  const n = Array.isArray(projectedPoints) ? projectedPoints.length : 0;
  const scores = new Array(n).fill(0);
  if (n < 3) return scores;

  for (let i = 1; i < n - 1; i += 1) {
    const a = projectedPoints[i - 1];
    const b = projectedPoints[i];
    const c = projectedPoints[i + 1];
    const v1x = b.x - a.x;
    const v1y = b.y - a.y;
    const v2x = c.x - b.x;
    const v2y = c.y - b.y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 <= 1e-3 || len2 <= 1e-3) continue;
    const dot = clampNumber(((v1x * v2x) + (v1y * v2y)) / (len1 * len2), -1, 1);
    const angle = Math.acos(dot); // 0..PI
    const normalized = clampNumber(angle / Math.PI, 0, 1);
    scores[i] = normalized;
  }

  const smoothed = new Array(n).fill(0);
  for (let i = 0; i < n; i += 1) {
    const prev = scores[Math.max(0, i - 1)] ?? 0;
    const curr = scores[i] ?? 0;
    const next = scores[Math.min(n - 1, i + 1)] ?? 0;
    smoothed[i] = clampNumber((prev * 0.25) + (curr * 0.5) + (next * 0.25), 0, 1);
  }
  return smoothed;
}

export function sampleLaneWaypoints(latLngPoints) {
  const points = (Array.isArray(latLngPoints) ? latLngPoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (points.length < 2) return points;
  const projected = projectLatLngToLocalMeters(points);
  if (projected.length < 2) return points;

  const minSpacingMeters = 10 * 0.3048;   // 10 ft
  const maxSpacingMeters = 200 * 0.3048;  // 200 ft
  const curvature = computeCurvatureScores(projected);
  const cumulativeMeters = new Array(projected.length).fill(0);
  for (let i = 1; i < projected.length; i += 1) {
    cumulativeMeters[i] = cumulativeMeters[i - 1] + getDistanceMetersXY(projected[i - 1], projected[i]);
  }

  const chosenIndices = [0];
  let lastChosen = 0;
  let localCurvePeak = 0;

  for (let i = 1; i < projected.length - 1; i += 1) {
    localCurvePeak = Math.max(localCurvePeak, curvature[i] ?? 0);
    const distanceFromLast = cumulativeMeters[i] - cumulativeMeters[lastChosen];

    const curveFactor = clampNumber(localCurvePeak * 1.35, 0, 1);
    const desiredSpacingMeters = maxSpacingMeters - ((maxSpacingMeters - minSpacingMeters) * curveFactor);

    if (distanceFromLast >= desiredSpacingMeters) {
      chosenIndices.push(i);
      lastChosen = i;
      localCurvePeak = 0;
    }
  }

  if (chosenIndices[chosenIndices.length - 1] !== projected.length - 1) {
    chosenIndices.push(projected.length - 1);
  }

  const deduped = [];
  chosenIndices.forEach((idx) => {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== idx) deduped.push(idx);
  });

  let sampled = deduped.map((idx) => ({ lat: points[idx].lat, lng: points[idx].lng }));
  const maxLaneWaypoints = 48;
  if (sampled.length > maxLaneWaypoints) {
    const n = sampled.length;
    const capped = [];
    for (let k = 0; k < maxLaneWaypoints; k += 1) {
      const i = Math.round((k / (maxLaneWaypoints - 1)) * (n - 1));
      capped.push(sampled[i]);
    }
    sampled = capped.filter((p, j, arr) => j === 0 || p.lat !== arr[j - 1].lat || p.lng !== arr[j - 1].lng);
  }
  return sampled;
}

export function getPathDisplayName(path, idx = 0) {
  const label = String(path?.label ?? '').trim();
  if (label) return label;
  return `Route${idx + 1}`;
}

export function getLaneDisplayName(lane, idx = 0) {
  const label = String(lane?.label ?? '').trim();
  if (label) return label;
  return `Lane ${idx + 1}`;
}

export function getSegmentDisplayName(seg, idx) {
  // Lanes carry a pathId pointing at their parent path; paths don't.
  // That's the only reliable shape difference, so anything without a
  // pathId (and not explicitly tagged 'lane') is treated as a path.
  if (seg?.pathId != null || seg?.type === 'lane') {
    return getLaneDisplayName(seg, idx);
  }
  return getPathDisplayName(seg, idx);
}

export const EMPTY_WAYPOINTS = [];
